import { GeneratedMap } from './TerrainGenerator';
import { EnemyType } from '../entities/Enemy';

export interface MissionLayout {
  playerSpawn: { x: number; y: number }[]; // tile coords for up to 4 soldiers
  enemySpawns: { x: number; y: number; type: string; waypoints?: { x: number; y: number }[] }[];
  objectives: {
    type: 'kill_all' | 'destroy_target_building';
    tile?: { x: number; y: number };
  }[];
  destroyTarget?: { x: number; y: number }; // tile to mark as destroy objective
  /** Optional explosive barrels — tile coords. */
  barrels?: { x: number; y: number }[];
  /** Optional ammo collectables — tile coords with type and amount. */
  ammoPickups?: { x: number; y: number; type: string; amount: number }[];
  /** Optional enemy buildings — tile coords. */
  enemyBuildings?: { x: number; y: number }[];
}

/** Simple seeded PRNG (Mulberry32). */
function makeRng(seed: number): () => number {
  let s = seed >>> 0;
  return (): number => {
    s += 0x6d2b79f5;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export class MissionOverlay {
  /**
   * Generate spawn positions and objectives for the given map and mission number.
   * If a valid layout can't be found within maxAttempts, returns the best fallback.
   */
  generate(map: GeneratedMap, missionNumber: number, seed?: number): MissionLayout {
    const MAX_ATTEMPTS = 10;
    for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
      const rng = makeRng((seed ?? 0) + attempt * 0x1337);
      const layout = this.tryGenerate(map, missionNumber, rng);
      if (layout) return layout;
    }
    // Fallback: guaranteed safe layout from fixed positions
    return this.fallbackLayout(map, missionNumber);
  }

  private tryGenerate(
    map: GeneratedMap,
    missionNumber: number,
    rng: () => number
  ): MissionLayout | null {
    const { width, height, tiles } = map;

    // ── Player spawns: walkable tiles in top-left quadrant (cols 2–8, rows 2–8) ──
    const playerCandidates = this.collectWalkable(
      tiles,
      2, Math.min(8, width - 2),
      2, Math.min(8, height - 2)
    );
    if (playerCandidates.length < 4) return null;

    const playerSpawn = this.pickSpread(playerCandidates, 4, 2, rng);
    if (playerSpawn.length < 1) return null;

    // ── Enemy spawns: right half of map ──
    const enemyCount = 3 + Math.min(missionNumber, 5);
    const halfCol = Math.floor(width / 2);
    const enemyCandidates = this.collectWalkable(
      tiles,
      halfCol, width - 2,
      1, height - 2
    );
    if (enemyCandidates.length < enemyCount) return null;

    const enemyPositions = this.pickSpread(enemyCandidates, enemyCount, 2, rng);
    if (enemyPositions.length < enemyCount) return null;

    // 1 rocket trooper per 3 riflemen (rounded)
    const enemySpawns: { x: number; y: number; type: string }[] = enemyPositions.map(
      (pos, i) => ({
        x: pos.x,
        y: pos.y,
        type: (i + 1) % 3 === 0 ? EnemyType.ROCKET_TROOPER : EnemyType.RIFLEMAN,
      })
    );

    // ── Destroy target: a wall tile in the enemy half ──
    const destroyTarget = this.findDestroyTarget(tiles, halfCol, width, height, rng);

    // ── BFS reachability: every enemy must be reachable from every player spawn ──
    for (const pSpawn of playerSpawn) {
      for (const eSpawn of enemySpawns) {
        if (!this.bfsReachable(tiles, pSpawn, { x: eSpawn.x, y: eSpawn.y }, width, height)) {
          return null;
        }
      }
    }

    const objectives: MissionLayout['objectives'] = [
      { type: 'kill_all' },
    ];
    if (destroyTarget) {
      objectives.push({
        type: 'destroy_target_building',
        tile: destroyTarget,
      });
    }

    return {
      playerSpawn,
      enemySpawns,
      objectives,
      destroyTarget: destroyTarget ?? undefined,
    };
  }

  /** Collect all walkable (index 1) tiles in the given column/row range. */
  private collectWalkable(
    tiles: number[][],
    colMin: number,
    colMax: number,
    rowMin: number,
    rowMax: number
  ): { x: number; y: number }[] {
    const result: { x: number; y: number }[] = [];
    for (let r = rowMin; r <= rowMax; r++) {
      for (let c = colMin; c <= colMax; c++) {
        if (tiles[r]?.[c] === 1 || tiles[r]?.[c] === 3) {
          result.push({ x: c, y: r });
        }
      }
    }
    return result;
  }

  /**
   * Pick up to `count` tiles from candidates, ensuring each picked tile is at
   * least `minDist` tiles from every other picked tile (Chebyshev distance).
   */
  private pickSpread(
    candidates: { x: number; y: number }[],
    count: number,
    minDist: number,
    rng: () => number
  ): { x: number; y: number }[] {
    // Shuffle
    const pool = [...candidates];
    for (let i = pool.length - 1; i > 0; i--) {
      const j = Math.floor(rng() * (i + 1));
      [pool[i], pool[j]] = [pool[j], pool[i]];
    }

    const picked: { x: number; y: number }[] = [];
    for (const candidate of pool) {
      if (picked.length >= count) break;
      const tooClose = picked.some(
        (p) => Math.max(Math.abs(p.x - candidate.x), Math.abs(p.y - candidate.y)) < minDist
      );
      if (!tooClose) picked.push(candidate);
    }
    return picked;
  }

  /** Find a wall tile (index 2) in the enemy half to use as the destroy target. */
  private findDestroyTarget(
    tiles: number[][],
    halfCol: number,
    width: number,
    height: number,
    rng: () => number
  ): { x: number; y: number } | null {
    const walls: { x: number; y: number }[] = [];
    for (let r = 1; r < height - 1; r++) {
      for (let c = halfCol; c < width - 1; c++) {
        if (tiles[r]?.[c] === 2) {
          walls.push({ x: c, y: r });
        }
      }
    }
    if (walls.length === 0) return null;
    return walls[Math.floor(rng() * walls.length)];
  }

  /**
   * BFS from `from` to `to` on walkable tiles.
   * Returns true if `to` is reachable from `from`.
   */
  private bfsReachable(
    tiles: number[][],
    from: { x: number; y: number },
    to: { x: number; y: number },
    width: number,
    height: number
  ): boolean {
    if (from.x === to.x && from.y === to.y) return true;

    const visited = new Set<string>();
    const queue: [number, number][] = [[from.y, from.x]];
    visited.add(`${from.y},${from.x}`);

    while (queue.length > 0) {
      const [r, c] = queue.shift()!;
      const neighbours: [number, number][] = [
        [r - 1, c],
        [r + 1, c],
        [r, c - 1],
        [r, c + 1],
      ];
      for (const [nr, nc] of neighbours) {
        if (nr < 0 || nr >= height || nc < 0 || nc >= width) continue;
        if (visited.has(`${nr},${nc}`)) continue;
        const tileVal = tiles[nr]?.[nc];
        if (tileVal !== 1 && tileVal !== 3) continue;
        if (nr === to.y && nc === to.x) return true;
        visited.add(`${nr},${nc}`);
        queue.push([nr, nc]);
      }
    }
    return false;
  }

  /** Guaranteed fallback using fixed tile positions. */
  private fallbackLayout(map: GeneratedMap, missionNumber: number): MissionLayout {
    const enemyCount = 3 + Math.min(missionNumber, 5);
    const halfCol = Math.floor(map.width / 2);

    const playerSpawn = [
      { x: 3, y: 3 },
      { x: 4, y: 3 },
      { x: 3, y: 4 },
      { x: 4, y: 4 },
    ];

    const enemySpawns: { x: number; y: number; type: string }[] = [];
    for (let i = 0; i < enemyCount; i++) {
      enemySpawns.push({
        x: halfCol + 2 + (i % 4),
        y: 3 + Math.floor(i / 4) * 3,
        type: (i + 1) % 3 === 0 ? EnemyType.ROCKET_TROOPER : EnemyType.RIFLEMAN,
      });
    }

    return {
      playerSpawn,
      enemySpawns,
      objectives: [{ type: 'kill_all' }],
    };
  }
}
